import type { Maybe } from '@adornis/base/utilTypes.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { DateTime } from 'luxon';
import { ChancenDynamicClassProduct } from '../../_cms/products/entities/ChancenDynamicClassProduct.js';
import { Course } from '../../courses/Course.js';
import { KPF } from '../../master-data-management/kpf/entities/KPF.js';
import { Partner } from '../../master-data-management/partner/entities/Partner.js';
import { ChancenwerkUser } from '../../master-data-management/users/entities/ChancenwerkUser.js';
import { Student } from '../../master-data-management/users/entities/Student.js';
import { PresenceRecord, SchuKoPresenceRecord, STATUS_TO_CSV, Status } from '../../PresenceRecord.js';
import { AttendanceOverviewResult } from '../entities/AttendanceOverviewResult.js';

const getAttendanceOverview = registerQuery({
  type: () => AttendanceOverviewResult,
  operationName: 'AttendanceAPI_getOverview',
  params: [{ type: () => String, name: 'userID' }],
  resolve: (userID: string) => {
    return async (gqlFields: BaseQLSelectionSet<AttendanceOverviewResult>) => {
      if (!context.serverContext) {
        const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
          roles: { name: 1, permissions: 1, contexts: 1 },
        });

        if (!user) {
          throw new Error('User not found');
        }
      }

      const presRecordCol = await getRawCollection<PresenceRecord>(PresenceRecord._collectionName);
      const schukoRecordCol = await getRawCollection<SchuKoPresenceRecord>(SchuKoPresenceRecord._collectionName);

      // Use aggregation to get only existing courses with attendance records for this user
      // Include archived courses because we need them for attendance overview
      const courseIdsAggregation = await presRecordCol
        .aggregate([
          {
            $match: {
              $or: [
                { 'studentRecordsUS.userID': userID },
                { 'studentRecordsOS.userID': userID },
                { 'lekoRecords.userID': userID },
              ],
              courseID: { $exists: true, $ne: null },
            },
          },
          { $lookup: { from: Course._collectionName, localField: 'courseID', foreignField: '_id', as: 'course' } },
          { $match: { course: { $ne: [] } } },
          { $project: { _id: 1 } },
        ])
        .toArray();

      const validRecordIds = courseIdsAggregation.map(r => r._id);

      // Parallel queries for both record types with optimized filtering
      const [presenceResults, schukoResults] = await Promise.all([
        // Get presence records with attendance stats in a single query
        presRecordCol
          .aggregate([
            {
              $match: {
                _id: { $in: validRecordIds },
                $or: [
                  { studentRecordsUS: { $elemMatch: { userID: userID, status: Status.PRESENT } } },
                  { studentRecordsOS: { $elemMatch: { userID: userID, status: Status.PRESENT } } },
                  { lekoRecords: { $elemMatch: { userID: userID, status: Status.PRESENT } } },
                ],
              },
            },
            {
              $project: {
                date: 1,
                hasPresent: {
                  $cond: {
                    if: {
                      $or: [
                        {
                          $anyElementTrue: {
                            $map: {
                              input: '$studentRecordsUS',
                              as: 'record',
                              in: {
                                $and: [
                                  { $eq: ['$$record.userID', userID] },
                                  { $eq: ['$$record.status', Status.PRESENT] },
                                ],
                              },
                            },
                          },
                        },
                        {
                          $anyElementTrue: {
                            $map: {
                              input: '$studentRecordsOS',
                              as: 'record',
                              in: {
                                $and: [
                                  { $eq: ['$$record.userID', userID] },
                                  { $eq: ['$$record.status', Status.PRESENT] },
                                ],
                              },
                            },
                          },
                        },
                        {
                          $anyElementTrue: {
                            $map: {
                              input: '$lekoRecords',
                              as: 'record',
                              in: {
                                $and: [
                                  { $eq: ['$$record.userID', userID] },
                                  { $eq: ['$$record.status', Status.PRESENT] },
                                ],
                              },
                            },
                          },
                        },
                      ],
                    },
                    then: 1,
                    else: 0,
                  },
                },
              },
            },
            { $group: { _id: null, lastAttendance: { $max: '$date' }, attendanceCount: { $sum: '$hasPresent' } } },
          ])
          .toArray(),

        // Get SchukO records with attendance stats in a single query
        schukoRecordCol
          .aggregate([
            { $match: { 'schukoRecords.userID': userID, 'schukoRecords.status': Status.PRESENT } },
            {
              $project: {
                date: 1,
                hasPresent: {
                  $cond: {
                    if: {
                      $anyElementTrue: {
                        $map: {
                          input: '$schukoRecords',
                          as: 'record',
                          in: {
                            $and: [{ $eq: ['$$record.userID', userID] }, { $eq: ['$$record.status', Status.PRESENT] }],
                          },
                        },
                      },
                    },
                    then: 1,
                    else: 0,
                  },
                },
              },
            },
            { $group: { _id: null, lastAttendance: { $max: '$date' }, attendanceCount: { $sum: '$hasPresent' } } },
          ])
          .toArray(),
      ]);

      // Extract results or use defaults
      const presenceStats = presenceResults[0] || { lastAttendance: null, attendanceCount: 0 };
      const schukoStats = schukoResults[0] || { lastAttendance: null, attendanceCount: 0 };

      // Determine the latest attendance date
      let lastAttendanceBoth: Maybe<Date> = null;
      if (presenceStats.lastAttendance && schukoStats.lastAttendance) {
        lastAttendanceBoth = DateTime.max(
          DateTime.fromJSDate(presenceStats.lastAttendance),
          DateTime.fromJSDate(schukoStats.lastAttendance),
        ).toJSDate();
      } else {
        lastAttendanceBoth = presenceStats.lastAttendance || schukoStats.lastAttendance;
      }

      // Return the combined results
      return new AttendanceOverviewResult({
        lastAttendance: lastAttendanceBoth,
        attendanceCount: (presenceStats.attendanceCount || 0) + (schukoStats.attendanceCount || 0),
      });
    };
  },
});

const exportAttendanceMonth = registerQuery({
  type: () => String,
  operationName: 'exportAttendanceMonth',
  resolve: (month: DateTime) => {
    return async (gqlFields: void) => {
      // Setup
      const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        roles: { name: 1, permissions: 1, contexts: 1 },
      });

      if (!user) {
        throw new Error('User not found');
      }

      const from = month.startOf('month');
      const till = month.endOf('month');

      const presRecordCol = await getRawCollection(PresenceRecord._collectionName);

      // * Single aggregation: presence records -> lookup course (startTime, kpfID) -> unwind user records
      // *   -> group by (userID, kpfID) -> lookup user info -> lookup KPF info
      const aggResult = await presRecordCol
        .aggregate<typeof aggResult>([
          // 1. Filter to month (uses { date: 1 } index) -> ~ 1.5k docs
          { $match: { _class: PresenceRecord._class, date: { $gte: from.toJSDate(), $lte: till.toJSDate() } } },

          // 2. Lookup course (only startTime + kpfID)
          {
            $lookup: {
              from: Course._collectionName,
              localField: 'courseID',
              foreignField: '_id',
              pipeline: [{ $project: { startTime: 1, kpfID: 1, schuKoIDs: 1 } }],
              as: '_course',
            },
          },
          { $unwind: { path: '$_course', preserveNullAndEmptyArrays: false } },

          // 3. Compute the "key" (dd.MM.yyyy_HH:mm) server-side
          {
            $addFields: {
              _key: {
                $concat: [
                  { $dateToString: { format: '%d.%m.%Y', date: '$date' } },
                  '_',
                  {
                    $cond: {
                      if: { $ifNull: ['$_course.startTime', false] },
                      // biome-ignore lint/suspicious/noThenProperty: this is a query
                      then: { $dateToString: { format: '%H:%M', date: '$_course.startTime' } },
                      else: '??:??',
                    },
                  },
                ],
              },
            },
          },

          // 4. Lookup SchuKo records for this (kpfID, date)
          {
            $lookup: {
              from: SchuKoPresenceRecord._collectionName,
              let: { kpfID: '$_course.kpfID', date: '$date' },
              pipeline: [
                {
                  $match: {
                    $expr: {
                      $and: [
                        { $eq: ['$_class', SchuKoPresenceRecord._class] },
                        { $eq: ['$kpfID', '$$kpfID'] },
                        { $eq: ['$date', '$$date'] },
                      ],
                    },
                  },
                },
                { $project: { schukoRecords: 1 } },
              ],
              as: '_schukoRecord',
            },
          },

          // 5. Unwind into individual user rows using $facet
          {
            $facet: {
              students: [
                {
                  $project: {
                    _key: 1,
                    date: 1,
                    kpfID: '$_course.kpfID',
                    records: {
                      $concatArrays: [{ $ifNull: ['$studentRecordsUS', []] }, { $ifNull: ['$studentRecordsOS', []] }],
                    },
                  },
                },
                { $unwind: '$records' },
                { $match: { 'records.userID': { $exists: true, $ne: null } } },
                {
                  $project: {
                    userID: '$records.userID',
                    status: '$records.status',
                    key: '$_key',
                    date: 1,
                    kpfID: 1,
                    type: { $literal: 'student' },
                  },
                },
              ],
              lekos: [
                { $project: { _key: 1, date: 1, kpfID: '$_course.kpfID', records: { $ifNull: ['$lekoRecords', []] } } },
                { $unwind: '$records' },
                { $match: { 'records.userID': { $exists: true, $ne: null } } },
                {
                  $project: {
                    userID: '$records.userID',
                    status: '$records.status',
                    key: '$_key',
                    date: 1,
                    kpfID: 1,
                    type: { $literal: 'leko' },
                  },
                },
              ],
              schukos: [
                { $match: { '_schukoRecord.0': { $exists: true } } },
                {
                  $project: {
                    _key: 1,
                    date: 1,
                    kpfID: '$_course.kpfID',
                    schuKoIDs: '$_course.schuKoIDs',
                    records: { $arrayElemAt: ['$_schukoRecord.schukoRecords', 0] },
                  },
                },
                { $unwind: '$records' },
                { $unwind: '$records' },
                { $match: { 'records.userID': { $exists: true, $ne: null } } },
                // Only include schukos that are either assigned to the course or have a non-NOT_SURE status
                {
                  $match: {
                    $or: [
                      { 'records.status': { $ne: Status.NOT_SURE } },
                      { $expr: { $in: ['$records.userID', { $ifNull: ['$schuKoIDs', []] }] } },
                    ],
                  },
                },
                {
                  $project: {
                    userID: '$records.userID',
                    status: '$records.status',
                    key: '$_key',
                    date: 1,
                    kpfID: 1,
                    type: { $literal: 'schuko' },
                  },
                },
              ],
            },
          },

          // 6. Union the facets
          { $project: { all: { $concatArrays: ['$students', '$lekos', '$schukos'] } } },
          { $unwind: '$all' },
          { $replaceRoot: { newRoot: '$all' } },

          // 7. Group by (userID, kpfID, type) -> collect all key+status entries per user
          {
            $group: {
              _id: { userID: '$userID', kpfID: '$kpfID', type: '$type' },
              entries: { $push: { key: '$key', status: '$status', date: '$date' } },
              assignedKeys: { $addToSet: '$key' },
            },
          },

          // 8. Lookup user info -> ONE batch $lookup
          {
            $lookup: {
              from: ChancenwerkUser._collectionName,
              localField: '_id.userID',
              foreignField: '_id',
              pipeline: [
                {
                  $project: {
                    _class: 1,
                    vorname: 1,
                    nachname: 1,
                    email: 1,
                    studentID: 1,
                    employeeID: 1,
                    'joinInformation.eintrittsdatum': 1,
                    'kuendigung.austrittsdatum': 1,
                  },
                },
              ],
              as: '_user',
            },
          },

          // 9. Lookup KPF -> then resolve school (Partner) and produkt (DynamicClassProduct)
          {
            $lookup: {
              from: KPF._collectionName,
              localField: '_id.kpfID',
              foreignField: '_id',
              pipeline: [
                { $project: { schoolID: 1, 'produkt._class': 1 } },
                // Resolve school -> Partner (kuerzel)
                {
                  $lookup: {
                    from: Partner._collectionName,
                    localField: 'schoolID',
                    foreignField: '_id',
                    pipeline: [{ $project: { kuerzel: 1 } }],
                    as: '_school',
                  },
                },
                // Resolve produkt._class -> DynamicClassProduct (kuerzel)
                {
                  $lookup: {
                    from: ChancenDynamicClassProduct._collectionName,
                    localField: 'produkt._class',
                    foreignField: 'class',
                    pipeline: [{ $project: { kuerzel: 1 } }],
                    as: '_produkt',
                  },
                },
                {
                  $project: {
                    schoolKuerzel: { $arrayElemAt: ['$_school.kuerzel', 0] },
                    produktKuerzel: { $arrayElemAt: ['$_produkt.kuerzel', 0] },
                  },
                },
              ],
              as: '_kpf',
            },
          },

          // 10. Flatten
          {
            $project: {
              _id: 0,
              userID: '$_id.userID',
              kpfID: '$_id.kpfID',
              type: '$_id.type',
              entries: 1,
              assignedKeys: 1,
              user: { $arrayElemAt: ['$_user', 0] },
              kpf: { $arrayElemAt: ['$_kpf', 0] },
            },
          },
        ])
        .toArray();

      // * Build time keys from all entries (sorted chronologically)
      const timeKeySet = new Set<string>();
      for (const row of aggResult) {
        for (const entry of row.entries) {
          timeKeySet.add(entry.key);
        }
      }
      const timeKeys = [...timeKeySet].sort((a, b) => {
        // Parse dd.MM.yyyy_HH:mm for proper chronological sort
        const [dateA, timeA] = a.split('_');
        const [dateB, timeB] = b.split('_');
        const [dA, mA, yA] = dateA!.split('.');
        const [dB, mB, yB] = dateB!.split('.');
        const cmp = `${yA}${mA}${dA}${timeA}`.localeCompare(`${yB}${mB}${dB}${timeB}`);
        return cmp;
      });

      // * Build CSV header
      const header = ['ID', 'Nachname', 'Vorname', 'KPF', 'Typ', ...timeKeys];

      // * Map aggregation results to CSV lines
      const lines: { kuerzel: string; line: string[] }[] = [];

      for (const row of aggResult) {
        if (!row.user || !row.kpf) continue;

        const kuerzel = (row.kpf.schoolKuerzel ?? 'X_X') + '_' + (row.kpf.produktKuerzel ?? 'X');
        const isStudent = row.type === 'student';
        const isSchuko = row.type === 'schuko';

        const typLabel = isStudent
          ? row.user._class === Student._class
            ? 'Schüler:in'
            : 'LeKo'
          : isSchuko
            ? 'SchuKo'
            : 'LeKo';

        const infos = [
          (row.user as any).studentID ?? (row.user as any).employeeID ?? '',
          row.user.nachname ?? (isStudent ? '' : (row.user.email ?? '')),
          row.user.vorname ?? '',
          kuerzel,
          typLabel,
        ];

        // Build a status-per-timeKey array
        // First, build a map of key -> best status (filtering NOT_SURE like the old code)
        const statusByKey: Record<string, string> = {};
        for (const entry of row.entries) {
          if (entry.status && entry.status !== Status.NOT_SURE) {
            statusByKey[entry.key] = STATUS_TO_CSV[entry.status as Status] ?? '';
          }
        }

        // Build a map of key -> date for edge-case logic
        const dateByKey: Record<string, Date> = {};
        for (const entry of row.entries) {
          if (!dateByKey[entry.key]) {
            dateByKey[entry.key] = entry.date;
          }
        }

        const dateCells = timeKeys.map(key => {
          // If there's a non-NOT_SURE status, use it
          if (statusByKey[key]) {
            return statusByKey[key]!;
          }

          // For students: handle eintrittsdatum/austrittsdatum edge cases
          if (isStudent) {
            const recordDate = dateByKey[key];
            const eintrittsdatum = row.user!.joinInformation?.eintrittsdatum;
            const austrittsdatum = row.user!.kuendigung?.austrittsdatum;

            if (recordDate && austrittsdatum && recordDate > austrittsdatum) {
              return ''; // after withdrawal
            }
            if (recordDate && eintrittsdatum && recordDate < eintrittsdatum) {
              return STATUS_TO_CSV[Status.NOT_YET_REGISTERED];
            }
          }

          // Check if user was assigned to this day at all (applies to all types)
          const wasAssigned = row.assignedKeys.includes(key);
          if (!wasAssigned) {
            return STATUS_TO_CSV[Status.SCHEDULED_ABSENCE];
          }

          return '';
        });

        lines.push({ kuerzel, line: [...infos, ...dateCells] });
      }

      // Sort by KPF kuerzel so all users of the same KPF are grouped together
      lines.sort((a, b) => a.kuerzel.localeCompare(b.kuerzel));

      return header.join(';') + '\n' + lines.map(l => l.line.join(';')).join('\n');
    };
  },
  params: [{ type: () => DateTime, name: 'month' }],
});

export namespace AttendanceAPI {
  export const getOverview = getAttendanceOverview;
  export const exportMonth = exportAttendanceMonth;
}
